import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store';
import { Card, MetricCard } from '../components/Card';
import { QuarterlyMonthlyToggle } from '../components/QuarterlyMonthlyToggle';
import { MonthlyBarChart } from '../components/MonthlyBarChart';
import { HistoryBadge } from '../components/HistoryBadge';
import { formatCompactCurrency, formatVariance } from '../utils/formatters';
import { getCurrentQuarterIndex, getQuarterStatus, getMonthStatus } from '../utils/fiscalYear';
import api from '../services/api';

// ============================================
// TYPE DEFINITIONS
// ============================================

type DepartmentTab = 'saas' | 'content-services';

interface TopDeal {
  name: string;
  value: number;
  confidence: string;
}

interface SaasRep {
  id: string;
  name: string;
  dealCount: number;
  pipeline: number;
  topDeal: TopDeal | null;
  march2026: number;
  fy26: number;
  fy27: number;
}

interface SaasDeal {
  id: string;
  name: string;
  repName: string;
  contractValue: number;
  weightedValue: number;
  confidence: string;
  timeline: string;
  category: string;
  status: string;
}

interface SaasCategory {
  name: string;
  pipeline: number;
  dealCount: number;
  percentage: number;
}

interface QuarterData {
  budget: number;
  forecast: number;
  actual: number;
  coverage: number;
}

interface MonthData {
  month: number;
  label: string;
  budget: number;
  forecast: number;
  actual: number;
}

interface Territory {
  id: string;
  name: string;
  repId: string;
  repName: string;
  q1: QuarterData;
  q2: QuarterData;
  q3: QuarterData;
  q4: QuarterData;
  q1Months?: MonthData[];
  q2Months?: MonthData[];
  q3Months?: MonthData[];
  q4Months?: MonthData[];
}

interface CSRep {
  id: string;
  name: string;
  territories: Territory[];
  streams: Array<Territory & { kind: 'stream' }>;
  totals: {
    budget: number;
    forecast: number;
    actual: number;
    avgCoverage: number;
  };
}

interface QuarterlyPerformance {
  quarter: string;
  budget: number;
  forecast: number;
  actual: number;
  coverage: number;
}

interface DepartmentDetailedData {
  config: {
    companyName: string;
    currentEBITDA: number;
    march2026Target: number;
    fy26Target: number;
    fy27Target: number;
  };
  saas: {
    overview: {
      totalPipeline: number;
      dealCount: number;
      repCount: number;
      avgDealSize: number;
    };
    targets: {
      march2026: { total: number; target: number; percentage: number; dealCount: number };
      fy26: { total: number; target: number; percentage: number; dealCount: number };
      fy27: { total: number; target: number; percentage: number; dealCount: number };
    };
    pipelineByConfidence: {
      high: number;
      medium: number;
      low: number;
    };
    categories: SaasCategory[];
    reps: SaasRep[];
    topDeals: SaasDeal[];
  };
  contentServices: {
    overview: {
      totalBudget: number;
      totalForecast: number;
      totalActual: number;
      territoryCount: number;
      repCount: number;
      avgCoverage: number;
    };
    quarterlyPerformance: QuarterlyPerformance[];
    monthlyPerformance?: MonthData[];
    reps: CSRep[];
    allTerritories: Territory[];
    revenueStreams?: (Territory & { kind: 'stream' })[];
  };
}

interface PeriodValues {
  budget: number;
  forecast: number;
  actual: number;
}

interface PeriodComparison {
  periodNumber: number;
  label: string;
  current: PeriodValues | null;
  previous: PeriodValues | null;
  delta: PeriodValues | null;
}

interface HistorySummary {
  asOf: string;
  baselineDate: string | null;
  daysSinceBaseline: number | null;
  hasBaseline: boolean;
  // legacy fields, kept for backward compat
  sinceDate: string | null;
  sinceDays: number | null;
  hasHistoricalData: boolean;
  companyByQuarter: PeriodComparison[];
  byTerritory: Array<{
    territoryCode: string;
    territoryName: string;
    quarters: PeriodComparison[];
    months: PeriodComparison[];
  }>;
}

/**
 * Lookup map: history.byTerritory[code].quarters or .months keyed by periodNumber.
 * Returns previous-actual delta for use with HistoryBadge.
 */
type HistoryLookup = {
  byTerritory: Map<string, { quarters: Map<number, PeriodComparison>; months: Map<number, PeriodComparison> }>;
  companyQuarters: Map<number, PeriodComparison>;
};

const buildHistoryLookup = (h: HistorySummary | null): HistoryLookup => {
  const lookup: HistoryLookup = { byTerritory: new Map(), companyQuarters: new Map() };
  if (!h) return lookup;
  for (const t of h.byTerritory) {
    const qMap = new Map<number, PeriodComparison>();
    const mMap = new Map<number, PeriodComparison>();
    t.quarters.forEach((q) => qMap.set(q.periodNumber, q));
    t.months.forEach((m) => mMap.set(m.periodNumber, m));
    lookup.byTerritory.set(t.territoryCode, { quarters: qMap, months: mMap });
  }
  h.companyByQuarter.forEach((q) => lookup.companyQuarters.set(q.periodNumber, q));
  return lookup;
};

// ============================================
// HELPER COMPONENTS
// ============================================

// Progress bar component
const ProgressBar: React.FC<{
  label: string;
  current: number;
  target: number;
  percentage: number;
  dealCount: number;
}> = ({ label, current, target, percentage, dealCount }) => {
  const cappedPercentage = Math.min(percentage, 100);
  const isOnTrack = percentage >= 80;

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium text-text-primary">{label}</span>
        <span className="text-xs text-text-muted">{dealCount} deals</span>
      </div>
      <div className="w-full bg-gray-700 rounded-full h-4 overflow-hidden">
        <div
          className={`h-4 rounded-full transition-all duration-500 ${
            isOnTrack ? 'bg-green-500' : 'bg-orange-500'
          }`}
          style={{ width: `${cappedPercentage}%` }}
        />
      </div>
      <div className="flex justify-between items-center mt-1">
        <span className="text-xs text-text-secondary">
          {formatCompactCurrency(current)} / {formatCompactCurrency(target)}
        </span>
        <span className={`text-xs font-semibold ${isOnTrack ? 'text-green-400' : 'text-orange-400'}`}>
          {percentage.toFixed(1)}%
        </span>
      </div>
    </div>
  );
};

// Confidence bar chart
const ConfidenceChart: React.FC<{
  high: number;
  medium: number;
  low: number;
}> = ({ high, medium, low }) => {
  const total = high + medium + low;
  const maxValue = Math.max(high, medium, low);

  const bars = [
    { label: 'HIGH', value: high, color: 'bg-green-500' },
    { label: 'MEDIUM', value: medium, color: 'bg-yellow-500' },
    { label: 'LOW', value: low, color: 'bg-red-500' },
  ];

  return (
    <div className="space-y-3">
      {bars.map((bar) => (
        <div key={bar.label} className="flex items-center gap-3">
          <span className="text-xs font-medium text-text-secondary w-16">{bar.label}</span>
          <div className="flex-1 bg-gray-700 rounded h-5 overflow-hidden">
            <div
              className={`h-5 ${bar.color} rounded transition-all duration-500`}
              style={{ width: maxValue > 0 ? `${(bar.value / maxValue) * 100}%` : '0%' }}
            />
          </div>
          <span className="text-sm font-medium text-text-primary w-20 text-right">
            {formatCompactCurrency(bar.value)}
          </span>
          <span className="text-xs text-text-muted w-12 text-right">
            {total > 0 ? `${((bar.value / total) * 100).toFixed(0)}%` : '0%'}
          </span>
        </div>
      ))}
    </div>
  );
};

// Quarterly chart for Content Services with past/current/future logic
const QuarterlyChart: React.FC<{
  data: QuarterlyPerformance[];
  /** Optional: previous-meeting actual per quarter (1-4) for inline ± deltas. */
  previousActualByQuarter?: Record<number, number | null>;
}> = ({ data, previousActualByQuarter }) => {
  const currentQuarter = getCurrentQuarterIndex();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {data.map((quarter, index) => {
        const quarterStatus = getQuarterStatus(index, currentQuarter);

        // Calculate second bar value based on quarter status
        const secondBarValue = quarterStatus === 'past' ? quarter.actual :
                              quarterStatus === 'future' ? quarter.forecast :
                              quarter.actual + quarter.forecast;

        const maxValue = Math.max(quarter.budget, secondBarValue, 1);
        const budgetWidth = (quarter.budget / maxValue) * 100;
        const actualWidth = (quarter.actual / maxValue) * 100;
        const forecastWidth = (quarter.forecast / maxValue) * 100;

        const isOnTrack = secondBarValue >= quarter.budget * 0.95;

        // Determine label for second bar
        const getSecondBarLabel = () => {
          if (quarterStatus === 'past') return 'Actual';
          if (quarterStatus === 'future') return 'Forecast';
          return 'Actual + Forecast';
        };

        return (
          <Card key={quarter.quarter}>
            <div className="flex justify-between items-start mb-3">
              <h4 className="font-semibold text-text-primary">{quarter.quarter}</h4>
              <div className="flex flex-col items-end gap-1">
                <span
                  className={`text-xs px-2 py-1 rounded ${
                    isOnTrack ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'
                  }`}
                >
                  {isOnTrack ? 'On Track' : 'Below Target'}
                </span>
                {previousActualByQuarter && previousActualByQuarter[index + 1] !== undefined && (
                  <HistoryBadge
                    current={quarter.actual}
                    previous={previousActualByQuarter[index + 1]}
                    label="vs last meeting"
                    showNeutral={false}
                  />
                )}
              </div>
            </div>
            <div className="space-y-3">
              {/* Budget Bar */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-blue-400 w-20">Budget</span>
                <div className="flex-1 bg-gray-700 rounded h-4 overflow-hidden">
                  <div
                    className="h-4 bg-blue-500 rounded"
                    style={{ width: `${budgetWidth}%` }}
                  />
                </div>
                <span className="text-xs text-text-primary w-16 text-right">
                  {formatCompactCurrency(quarter.budget)}
                </span>
              </div>

              {/* Second Bar - varies based on quarter status */}
              <div className="flex items-center gap-2">
                <span className={`text-xs w-20 ${quarterStatus === 'past' ? 'text-green-400' : quarterStatus === 'future' ? 'text-orange-400' : 'text-white'}`}>
                  {getSecondBarLabel()}
                </span>
                <div className="flex-1 bg-gray-700 rounded h-4 overflow-hidden flex">
                  {quarterStatus === 'past' && (
                    <div
                      className="h-4 bg-green-500 rounded"
                      style={{ width: `${actualWidth}%` }}
                    />
                  )}
                  {quarterStatus === 'current' && (
                    <>
                      <div
                        className="h-4 bg-green-500"
                        style={{ width: `${actualWidth}%` }}
                      />
                      <div
                        className="h-4 bg-orange-500"
                        style={{ width: `${forecastWidth}%` }}
                      />
                    </>
                  )}
                  {quarterStatus === 'future' && (
                    <div
                      className="h-4 bg-orange-500 rounded"
                      style={{ width: `${forecastWidth}%` }}
                    />
                  )}
                </div>
                <span className="text-xs text-text-primary w-16 text-right">
                  {formatCompactCurrency(secondBarValue)}
                </span>
              </div>

              {/* Variance vs budget — sum of (actual+forecast) − budget */}
              <div className="mt-2 text-center">
                <span className={`text-xs font-semibold ${secondBarValue - quarter.budget >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatVariance(secondBarValue - quarter.budget)}
                </span>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const DepartmentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN';
  const [activeTab, setActiveTab] = useState<DepartmentTab>('saas');
  const [data, setData] = useState<DepartmentDetailedData | null>(null);
  const [history, setHistory] = useState<HistorySummary | null>(null);
  const [granularity, setGranularity] = useState<'quarterly' | 'monthly'>('quarterly');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [dashResponse, histResponse] = await Promise.all([
          api.get('/dashboard/departments-detailed', { params: { granularity } }),
          api.get('/dashboard/history/summary', { params: { sinceDays: 30 } }).catch(() => ({ data: null })),
        ]);
        if (dashResponse.data.success) {
          setData(dashResponse.data.data);
        } else {
          setError('Failed to load department data');
        }
        if (histResponse?.data?.success) {
          setHistory(histResponse.data.data);
        }
      } catch (err) {
        setError('Failed to load department data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [granularity]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger text-lg">{error || 'No data available'}</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'saas' as DepartmentTab, label: 'SaaS Sales', icon: '☁️' },
    { id: 'content-services' as DepartmentTab, label: 'Content Services', icon: '📄' },
  ];

  return (
    <div className="bg-background text-text-primary">
      {/* Header */}
      <header className="bg-surface border-b border-border">
        <div className="max-w-full mx-auto px-12 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-primary">Department Performance</h1>
              <p className="text-text-secondary mt-1">
                Path to All Targets: March 2026 ({formatCompactCurrency(data.config.march2026Target)}) |
                FY26 ({formatCompactCurrency(data.config.fy26Target)}) |
                FY27 ({formatCompactCurrency(data.config.fy27Target)})
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-text-muted">Current EBITDA</p>
              <p className="text-2xl font-bold text-success">
                {formatCompactCurrency(data.config.currentEBITDA)}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-surface border-b border-border">
        <div className="max-w-full mx-auto px-12">
          <div className="flex gap-2 py-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-full mx-auto px-12 py-8">
        {/* SaaS Tab Content */}
        {activeTab === 'saas' && (
          <div className="space-y-8">
            {/* Overview Metrics */}
            <section>
              <h2 className="text-xl font-semibold text-text-primary mb-4">SaaS Sales Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard
                  title="Total Pipeline"
                  value={formatCompactCurrency(data.saas.overview.totalPipeline)}
                  subtitle="Weighted value"
                  color="primary"
                />
                <MetricCard
                  title="Total Deals"
                  value={data.saas.overview.dealCount}
                  subtitle="Active opportunities"
                  color="success"
                />
                <MetricCard
                  title="Sales Reps"
                  value={data.saas.overview.repCount}
                  subtitle="Active reps"
                  color="warning"
                />
                <MetricCard
                  title="Avg Deal Size"
                  value={formatCompactCurrency(data.saas.overview.avgDealSize)}
                  subtitle="Per deal"
                  color="primary"
                />
              </div>
            </section>

            {/* Progress to Targets */}
            <section>
              <h2 className="text-xl font-semibold text-text-primary mb-4">Progress to Targets</h2>
              <Card>
                <ProgressBar
                  label="March 2026"
                  current={data.saas.targets.march2026.total}
                  target={data.saas.targets.march2026.target}
                  percentage={data.saas.targets.march2026.percentage}
                  dealCount={data.saas.targets.march2026.dealCount}
                />
                <ProgressBar
                  label="FY 2026 (Apr-Jun)"
                  current={data.saas.targets.fy26.total}
                  target={data.saas.targets.fy26.target}
                  percentage={data.saas.targets.fy26.percentage}
                  dealCount={data.saas.targets.fy26.dealCount}
                />
                <ProgressBar
                  label="FY 2027 (Full Year)"
                  current={data.saas.targets.fy27.total}
                  target={data.saas.targets.fy27.target}
                  percentage={data.saas.targets.fy27.percentage}
                  dealCount={data.saas.targets.fy27.dealCount}
                />
              </Card>
            </section>

            {/* Pipeline by Confidence & Categories */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section>
                <h2 className="text-xl font-semibold text-text-primary mb-4">Pipeline by Confidence</h2>
                <Card>
                  <ConfidenceChart
                    high={data.saas.pipelineByConfidence.high}
                    medium={data.saas.pipelineByConfidence.medium}
                    low={data.saas.pipelineByConfidence.low}
                  />
                </Card>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-text-primary mb-4">Category Breakdown</h2>
                <div className="grid grid-cols-1 gap-4">
                  {data.saas.categories.map((cat) => (
                    <Card key={cat.name}>
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-semibold text-primary">{cat.name}</h3>
                          <p className="text-sm text-text-muted">{cat.dealCount} deals</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-text-primary">
                            {formatCompactCurrency(cat.pipeline)}
                          </p>
                          <p className="text-sm text-text-secondary">{cat.percentage.toFixed(1)}%</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </section>
            </div>

            {/* Rep Performance */}
            <section>
              <h2 className="text-xl font-semibold text-text-primary mb-4">Rep Performance</h2>
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Rep</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">Deals</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">Pipeline</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Top Deal</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">Mar 2026</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">FY26</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">FY27</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-text-secondary"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.saas.reps.map((rep) => (
                        <tr
                          key={rep.id}
                          className="border-b border-border hover:bg-surface-light transition-colors cursor-pointer"
                          onClick={() => navigate(`/individual/${rep.id}`)}
                        >
                          <td className="py-4 px-4">
                            <p className="font-medium text-text-primary">{rep.name}</p>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary bg-opacity-20 text-primary text-sm font-medium">
                              {rep.dealCount}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <p className="font-bold text-primary">{formatCompactCurrency(rep.pipeline)}</p>
                          </td>
                          <td className="py-4 px-4">
                            {rep.topDeal ? (
                              <div>
                                <p className="text-sm text-text-primary truncate max-w-[150px]">
                                  {rep.topDeal.name}
                                </p>
                                <p className="text-xs text-text-muted">
                                  {formatCompactCurrency(rep.topDeal.value)} ({rep.topDeal.confidence})
                                </p>
                              </div>
                            ) : (
                              <span className="text-text-muted">-</span>
                            )}
                          </td>
                          <td className="py-4 px-4 text-right">
                            <p className="text-sm text-text-primary">{formatCompactCurrency(rep.march2026)}</p>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <p className="text-sm text-warning">{formatCompactCurrency(rep.fy26)}</p>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <p className="text-sm text-success">{formatCompactCurrency(rep.fy27)}</p>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="text-text-muted">→</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </section>

            {/* Top 10 Deals */}
            <section>
              <h2 className="text-xl font-semibold text-text-primary mb-4">Top 10 Deals</h2>
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Deal</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Rep</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">Value</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-text-secondary">Confidence</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Timeline</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.saas.topDeals.map((deal, index) => (
                        <tr key={deal.id} className="border-b border-border hover:bg-surface-light transition-colors">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-text-muted">#{index + 1}</span>
                              <p className="font-medium text-text-primary">{deal.name}</p>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <p className="text-sm text-text-secondary">{deal.repName}</p>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <p className="font-bold text-primary">{formatCompactCurrency(deal.weightedValue)}</p>
                            <p className="text-xs text-text-muted">
                              ({formatCompactCurrency(deal.contractValue)} raw)
                            </p>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                deal.confidence === 'HIGH'
                                  ? 'bg-green-500/20 text-green-400'
                                  : deal.confidence === 'MEDIUM'
                                  ? 'bg-yellow-500/20 text-yellow-400'
                                  : 'bg-red-500/20 text-red-400'
                              }`}
                            >
                              {deal.confidence}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <p className="text-sm text-text-secondary">{deal.timeline}</p>
                          </td>
                          <td className="py-4 px-4">
                            <p className="text-sm text-text-secondary">{deal.category}</p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </section>

            {/* View Full Dashboard Link */}
            <div className="flex justify-center">
              <button
                onClick={() => navigate('/team-summary')}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                View Team Summary →
              </button>
            </div>
          </div>
        )}

        {/* Content Services Tab Content */}
        {activeTab === 'content-services' && (() => {
          const histLookup = buildHistoryLookup(history);
          // Build maps of previous-actual values keyed by quarter / month for the
          // company-wide aggregate charts (sum across all territories' baselines).
          const companyPrevByQuarter: Record<number, number | null> = {};
          history?.companyByQuarter.forEach((q) => {
            companyPrevByQuarter[q.periodNumber] = q.previous?.actual ?? null;
          });
          // Aggregate previous-actual per month across all territories (only set if
          // every territory has a baseline for that month).
          const companyPrevByMonth: Record<number, number | null> = {};
          if (history) {
            for (let m = 1; m <= 12; m++) {
              const baselines = history.byTerritory
                .map((t) => t.months.find((x) => x.periodNumber === m)?.previous?.actual)
                .filter((v): v is number => typeof v === 'number');
              const allHave = baselines.length === history.byTerritory.length && history.byTerritory.length > 0;
              companyPrevByMonth[m] = allHave ? baselines.reduce((s, v) => s + v, 0) : null;
            }
          }
          void histLookup;

          return (
          <div className="space-y-8">
            {/* Granularity toggle + meeting-baseline status & Set Baseline button */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-text-muted">
                {history?.hasBaseline ? (
                  <>
                    Δ shown vs last meeting baseline:{' '}
                    <span className="text-text-primary font-medium">
                      {history.baselineDate ? new Date(history.baselineDate).toLocaleDateString() : '—'}
                    </span>
                    {' '}
                    ({history.daysSinceBaseline ?? 0} days ago)
                  </>
                ) : (
                  <>No meeting baseline set yet — Δ will appear after first baseline.</>
                )}
              </div>
              <div className="flex items-center gap-3">
                {isAdmin && (
                  <button
                    onClick={async () => {
                      if (!confirm('Mark current values as the new meeting baseline? (Δ comparisons will use this point going forward.)')) return;
                      try {
                        setMarking(true);
                        await api.post('/dashboard/mark-baseline');
                        const histResponse = await api.get('/dashboard/history/summary');
                        if (histResponse?.data?.success) setHistory(histResponse.data.data);
                      } catch (e) {
                        console.error(e);
                        alert('Failed to mark baseline');
                      } finally {
                        setMarking(false);
                      }
                    }}
                    disabled={marking}
                    className="px-3 py-2 text-sm font-medium rounded-lg bg-surface border border-border text-text-primary hover:bg-surface-light transition-colors disabled:opacity-50"
                    title="Capture current values as the new meeting baseline"
                  >
                    {marking ? 'Marking…' : 'Set new baseline'}
                  </button>
                )}
                <QuarterlyMonthlyToggle value={granularity} onChange={setGranularity} />
              </div>
            </div>

            {/* Overview Metrics — Performance to Date */}
            <section>
              <h2 className="text-xl font-semibold text-text-primary mb-4">Content Services Overview</h2>
              {(() => {
                const currentQ = getCurrentQuarterIndex();
                const qp = data.contentServices.quarterlyPerformance;

                let budgetToDate = 0;
                let progressToDate = 0;
                let fullYearProgress = 0;
                const fullYearBudget = qp.reduce((sum, q) => sum + q.budget, 0);

                qp.forEach((q, i) => {
                  const status = getQuarterStatus(i, currentQ);
                  const qProgress = status === 'past' ? q.actual :
                                    status === 'current' ? q.actual + q.forecast :
                                    q.forecast;
                  fullYearProgress += qProgress;

                  if (status === 'past' || status === 'current') {
                    budgetToDate += q.budget;
                    progressToDate += status === 'past' ? q.actual : q.actual + q.forecast;
                  }
                });

                const varianceToDate = progressToDate - budgetToDate;
                const variancePct = budgetToDate > 0 ? (progressToDate / budgetToDate * 100) : 0;
                const coveragePct = fullYearBudget > 0 ? (fullYearProgress / fullYearBudget * 100) : 0;

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <MetricCard
                      title="Budget to Date"
                      value={formatCompactCurrency(budgetToDate)}
                      subtitle="Past + current quarters"
                      color="primary"
                    />
                    <MetricCard
                      title="Progress to Date"
                      value={formatCompactCurrency(progressToDate)}
                      subtitle="Actual + forecast (current Q)"
                      color="success"
                    />
                    <MetricCard
                      title="Variance to Date"
                      value={formatVariance(varianceToDate)}
                      subtitle={`${variancePct.toFixed(1)}% of budget`}
                      trend={varianceToDate >= 0 ? 'up' : 'down'}
                      color={varianceToDate >= 0 ? 'success' : 'danger'}
                    />
                    <MetricCard
                      title="Full Year Outlook"
                      value={`${formatCompactCurrency(fullYearProgress)} / ${formatCompactCurrency(fullYearBudget)}`}
                      subtitle={`${coveragePct.toFixed(1)}% coverage`}
                      color="warning"
                    />
                  </div>
                );
              })()}
            </section>

            {/* Average Coverage */}
            <section>
              <Card>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">Average Coverage</h3>
                    <p className="text-sm text-text-muted">Across all territories and quarters</p>
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-4xl font-bold ${
                        data.contentServices.overview.avgCoverage >= 95 ? 'text-green-400' : 'text-orange-400'
                      }`}
                    >
                      {data.contentServices.overview.avgCoverage.toFixed(1)}%
                    </p>
                    <p className="text-sm text-text-muted">
                      {data.contentServices.overview.avgCoverage >= 95 ? 'On Track' : 'Below Target'}
                    </p>
                  </div>
                </div>
              </Card>
            </section>

            {/* Quarterly / Monthly Performance Charts */}
            <section>
              <h2 className="text-xl font-semibold text-text-primary mb-4">
                {granularity === 'monthly' ? 'Monthly Performance' : 'Quarterly Performance'}
              </h2>
              {granularity === 'monthly' ? (
                (() => {
                  const monthly = data.contentServices.monthlyPerformance || [];
                  const hasData = monthly.some(m => m.budget > 0 || m.actual > 0 || m.forecast > 0);
                  if (!hasData) {
                    return (
                      <Card>
                        <p className="text-sm text-text-muted text-center py-4">
                          No monthly data available yet. Populate the <code className="text-text-primary">Territory_Monthly</code> sheet tab in Google Sheets, or wait for the actuals platform API integration.
                        </p>
                      </Card>
                    );
                  }
                  // Sort months in fiscal year order (Jul → Jun)
                  const fiscalOrder = [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6];
                  const sorted = [...monthly].sort((a, b) => fiscalOrder.indexOf(a.month) - fiscalOrder.indexOf(b.month));
                  return <MonthlyBarChart data={sorted} previousActualByMonth={companyPrevByMonth} />;
                })()
              ) : (
                <QuarterlyChart data={data.contentServices.quarterlyPerformance} previousActualByQuarter={companyPrevByQuarter} />
              )}
            </section>

            {/* Rep Performance with Territories */}
            <section>
              <h2 className="text-xl font-semibold text-text-primary mb-4">Rep Performance</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {(() => {
                  const currentQ = getCurrentQuarterIndex();

                  const repsWithVariance = data.contentServices.reps.map((rep) => {
                    let repBudget = 0;
                    let repActual = 0;
                    let repForecast = 0;

                    const ownedRows = [...rep.territories, ...(rep.streams || [])];
                    ownedRows.forEach((t) => {
                      const quarters = [t.q1, t.q2, t.q3, t.q4];
                      quarters.forEach((q, i) => {
                        const status = getQuarterStatus(i, currentQ);
                        repBudget += q.budget;
                        repActual += q.actual;
                        // Only count forecast for current + future quarters
                        if (status === 'current' || status === 'future') {
                          repForecast += q.forecast;
                        }
                      });
                    });

                    const repProgress = repActual + repForecast;
                    const variance = repProgress - repBudget;
                    return { rep, repBudget, repActual, repForecast, repProgress, variance };
                  });

                  repsWithVariance.sort((a, b) => a.variance - b.variance);

                  return repsWithVariance.map(({ rep, repBudget, repActual, repForecast, repProgress, variance }) => {
                    const progressPct = repBudget > 0 ? (repProgress / repBudget) * 100 : 0;
                    const cappedPct = Math.min(progressPct, 100);
                    const isOnTrack = progressPct >= 95;

                    const coverageBadge = rep.totals.avgCoverage >= 95
                      ? 'bg-green-500/20 text-green-400'
                      : rep.totals.avgCoverage >= 80
                      ? 'bg-orange-500/20 text-orange-400'
                      : 'bg-red-500/20 text-red-400';

                    return (
                      <Card key={rep.id}>
                        <div
                          className="cursor-pointer"
                          onClick={() => navigate(`/individual/${rep.id}`)}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="font-semibold text-primary text-lg">{rep.name}</h3>
                              <p className="text-sm text-text-muted">
                                {rep.territories.length} territories
                                {rep.streams && rep.streams.length > 0 && ` + ${rep.streams.length} stream${rep.streams.length === 1 ? '' : 's'}`}
                              </p>
                            </div>
                            <span className={`px-3 py-1 rounded text-sm font-medium ${coverageBadge}`}>
                              {rep.totals.avgCoverage.toFixed(1)}%
                            </span>
                          </div>

                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-text-muted">Budget</p>
                              <p className="text-sm font-semibold text-blue-400">
                                {formatCompactCurrency(repBudget)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-text-muted">Actual</p>
                              <p className="text-sm font-semibold text-green-400">
                                {formatCompactCurrency(repActual)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-text-muted">Forecast</p>
                              <p className="text-sm font-semibold text-orange-400">
                                {formatCompactCurrency(repForecast)}
                              </p>
                            </div>
                          </div>

                          {/* Progress bar */}
                          <div className="mb-4">
                            <div className="w-full bg-gray-700 rounded-full h-4 overflow-hidden">
                              <div
                                className={`h-4 rounded-full transition-all duration-500 ${isOnTrack ? 'bg-green-500' : 'bg-orange-500'}`}
                                style={{ width: `${cappedPct}%` }}
                              />
                            </div>
                            <div className="flex justify-between items-center mt-1">
                              <span className="text-xs text-text-secondary">
                                {formatCompactCurrency(repProgress)} / {formatCompactCurrency(repBudget)}
                              </span>
                              <span className={`text-xs font-semibold ${variance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {formatVariance(variance)}
                              </span>
                            </div>
                          </div>

                          <div className="border-t border-border pt-3">
                            <p className="text-xs text-text-muted mb-2">Territories:</p>
                            <div className="flex flex-wrap gap-2">
                              {rep.territories.map((t) => (
                                <span
                                  key={t.id}
                                  className="px-2 py-1 bg-gray-800 rounded text-xs text-text-secondary"
                                >
                                  {t.name}
                                </span>
                              ))}
                              {(rep.streams || []).map((s) => (
                                <span
                                  key={`stream-${s.id}`}
                                  className="px-2 py-1 bg-blue-900/40 rounded text-xs text-blue-300"
                                  title="Revenue stream"
                                >
                                  {s.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  });
                })()}
              </div>
            </section>

            {/* Territory Summary Table */}
            <section>
              <h2 className="text-xl font-semibold text-text-primary mb-4">
                Territory Summary
              </h2>
              <Card>
                <div className="overflow-x-auto">
                  {granularity === 'monthly' ? (() => {
                    // Monthly view: 12 month columns showing actual only.
                    const fiscalOrder = [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6];
                    const monthLabels: Record<number, string> = {
                      1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun',
                      7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec',
                    };
                    const getMonthData = (t: Territory, m: number): MonthData | undefined => {
                      const allMonths = [...(t.q1Months || []), ...(t.q2Months || []), ...(t.q3Months || []), ...(t.q4Months || [])];
                      return allMonths.find((x) => x.month === m);
                    };
                    const getMonthValue = (t: Territory, m: number): number | undefined => {
                      const md = getMonthData(t, m);
                      if (!md) return undefined;
                      // Use actual+forecast for current/future months, actual for past
                      const status = getMonthStatus(m);
                      return status === 'past' ? md.actual : status === 'future' ? md.forecast : md.actual + md.forecast;
                    };
                    // Combine territories + global revenue streams. Stream rows are
                    // tagged with kind: 'stream' so we can show a small badge.
                    const streams = (data.contentServices.revenueStreams || []) as Array<Territory & { kind: 'stream' }>;
                    const allRows: Array<Territory & { kind?: 'stream' }> = [
                      ...data.contentServices.allTerritories,
                      ...streams,
                    ];
                    // Sort by total monthly actuals (worst first to surface attention)
                    const sortedTerritories = [...allRows].sort((a, b) => {
                      const sumA = fiscalOrder.reduce((s, m) => s + (getMonthValue(a, m) || 0), 0);
                      const sumB = fiscalOrder.reduce((s, m) => s + (getMonthValue(b, m) || 0), 0);
                      return sumA - sumB;
                    });
                    const monthlyAgg = data.contentServices.monthlyPerformance || [];
                    return (
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-3 text-sm font-medium text-text-secondary sticky left-0 bg-surface">Territory</th>
                            <th className="text-left py-3 px-3 text-sm font-medium text-text-secondary">Rep</th>
                            {fiscalOrder.map((m) => (
                              <th key={m} className="text-right py-3 px-2 text-xs font-medium text-text-secondary">{monthLabels[m]}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedTerritories.map((t) => (
                            <tr key={t.id} className="border-b border-border hover:bg-surface-light transition-colors">
                              <td className="py-3 px-3 sticky left-0 bg-surface">
                                <p className="font-medium text-text-primary">
                                  {t.name}
                                  {t.kind === 'stream' && (
                                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">Stream</span>
                                  )}
                                </p>
                              </td>
                              <td className="py-3 px-3">
                                <p className="text-sm text-text-secondary">{t.repName}</p>
                              </td>
                              {fiscalOrder.map((m) => {
                                const md = getMonthData(t, m);
                                const v = getMonthValue(t, m);
                                if (!md || (!v && !md.budget)) {
                                  return <td key={m} className="py-3 px-2 text-right"><span className="text-xs text-text-muted">—</span></td>;
                                }
                                const colorClass = (v ?? 0) >= md.budget ? 'text-green-400' : 'text-red-400';
                                return (
                                  <td key={m} className="py-3 px-2 text-right">
                                    <p className={`text-xs font-medium ${colorClass}`}>{formatCompactCurrency(v ?? 0)}</p>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-surface-light font-semibold">
                            <td className="py-3 px-3 text-text-primary sticky left-0 bg-surface" colSpan={2}>Totals</td>
                            {fiscalOrder.map((m) => {
                              const total = monthlyAgg.find((x) => x.month === m)?.actual ?? 0;
                              return (
                                <td key={m} className="py-3 px-2 text-right">
                                  <span className="text-xs text-text-primary">{total > 0 ? formatCompactCurrency(total) : '—'}</span>
                                </td>
                              );
                            })}
                          </tr>
                        </tfoot>
                      </table>
                    );
                  })() : (() => {
                    const currentQ = getCurrentQuarterIndex();

                    const getSecondLabel = (qIndex: number): string => {
                      const status = getQuarterStatus(qIndex, currentQ);
                      if (status === 'past') return 'Actual';
                      if (status === 'future') return 'Forecast';
                      return 'Act+Fcast';
                    };

                    const getSecondValue = (q: QuarterData, qIndex: number): number => {
                      const status = getQuarterStatus(qIndex, currentQ);
                      if (status === 'past') return q.actual;
                      if (status === 'current') return q.actual + q.forecast;
                      return q.forecast;
                    };

                    const getProgressColor = (progress: number, budget: number): string => {
                      return progress >= budget ? 'text-green-400' : 'text-red-400';
                    };

                    const getSecondBg = (qIndex: number): string => {
                      const status = getQuarterStatus(qIndex, currentQ);
                      if (status === 'past') return 'bg-green-500/5';
                      if (status === 'current') return 'bg-green-500/5';
                      return 'bg-orange-500/5';
                    };

                    const getSecondHeaderBg = (qIndex: number): string => {
                      const status = getQuarterStatus(qIndex, currentQ);
                      if (status === 'past') return 'bg-green-500/10';
                      if (status === 'current') return 'bg-green-500/10';
                      return 'bg-orange-500/10';
                    };

                    // Combine territories + revenue streams as table rows
                    const streams = (data.contentServices.revenueStreams || []) as Array<Territory & { kind: 'stream' }>;
                    const allRows: Array<Territory & { kind?: 'stream' }> = [
                      ...data.contentServices.allTerritories,
                      ...streams,
                    ];
                    const territoriesWithVariance = allRows.map((t) => {
                      const quarters = [t.q1, t.q2, t.q3, t.q4];
                      const totalVar = quarters.reduce((sum, q, i) => {
                        return sum + (getSecondValue(q, i) - q.budget);
                      }, 0);
                      return { t, totalVar };
                    });

                    territoriesWithVariance.sort((a, b) => a.totalVar - b.totalVar);

                    const quarterLabels = ['Q1', 'Q2', 'Q3', 'Q4'];

                    return (
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Territory</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Rep</th>
                            {quarterLabels.map((label, i) => (
                              <React.Fragment key={label}>
                                <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary bg-blue-500/10">{label} Budget</th>
                                <th className={`text-right py-3 px-4 text-sm font-medium text-text-secondary ${getSecondHeaderBg(i)}`}>{label} {getSecondLabel(i)}</th>
                              </React.Fragment>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {territoriesWithVariance.map(({ t }) => {
                            const quarters = [t.q1, t.q2, t.q3, t.q4];
                            return (
                              <tr key={t.id} className="border-b border-border hover:bg-surface-light transition-colors">
                                <td className="py-3 px-4">
                                  <p className="font-medium text-text-primary">
                                    {t.name}
                                    {(t as any).kind === 'stream' && (
                                      <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">Stream</span>
                                    )}
                                  </p>
                                </td>
                                <td className="py-3 px-4">
                                  <p className="text-sm text-text-secondary">{t.repName}</p>
                                </td>
                                {quarters.map((q, i) => {
                                  const progress = getSecondValue(q, i);
                                  return (
                                    <React.Fragment key={i}>
                                      <td className="py-3 px-4 text-right bg-blue-500/5">
                                        <p className="text-sm text-text-primary">{formatCompactCurrency(q.budget)}</p>
                                      </td>
                                      <td className={`py-3 px-4 text-right ${getSecondBg(i)}`}>
                                        <p className={`text-sm font-medium ${getProgressColor(progress, q.budget)}`}>{formatCompactCurrency(progress)}</p>
                                      </td>
                                    </React.Fragment>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="bg-surface-light font-semibold">
                            <td className="py-3 px-4 text-text-primary" colSpan={2}>Totals</td>
                            {[0, 1, 2, 3].map((i) => {
                              const quarters = data.contentServices.allTerritories.map((t) => [t.q1, t.q2, t.q3, t.q4][i]);
                              const budgetTotal = quarters.reduce((sum, q) => sum + q.budget, 0);
                              const progressTotal = quarters.reduce((sum, q) => sum + getSecondValue(q, i), 0);
                              return (
                                <React.Fragment key={i}>
                                  <td className="py-3 px-4 text-right bg-blue-500/10">
                                    <span className="text-text-primary">{formatCompactCurrency(budgetTotal)}</span>
                                  </td>
                                  <td className={`py-3 px-4 text-right ${getSecondHeaderBg(i)}`}>
                                    <span className={`font-medium ${getProgressColor(progressTotal, budgetTotal)}`}>{formatCompactCurrency(progressTotal)}</span>
                                  </td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        </tfoot>
                      </table>
                    );
                  })()}
                </div>
              </Card>
            </section>

            {/* View Full Dashboard Link */}
            <div className="flex justify-center">
              <button
                onClick={() => navigate('/territories')}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                View Territory Dashboard →
              </button>
            </div>
          </div>
          );
        })()}
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-border bg-surface">
        <div className="max-w-full mx-auto px-12 py-4">
          <p className="text-center text-sm text-text-muted">
            {data.config.companyName} - Sales Dashboard © 2025
          </p>
        </div>
      </footer>
    </div>
  );
};
